/** One stable notification owner per account/origin. Focus never steals a call. */
export function ownNotifications(userId: number, changed: (primary: boolean) => void) {
  const id = crypto.randomUUID();
  const key = `skrepka.main-window.${userId}`;
  let primary = false, stopped = false, release: (() => void) | undefined;
  let timer: ReturnType<typeof setInterval> | undefined;
  let settle: ReturnType<typeof setTimeout> | undefined;
  const abort = new AbortController();
  const set = (value: boolean) => { if (!stopped && primary !== value) { primary = value; changed(value); } };
  const read = () => {
    try { const v: unknown = JSON.parse(localStorage.getItem(key) || 'null'); return v && typeof v === 'object' && 'id' in v && typeof v.id === 'string' && 'until' in v && typeof v.until === 'number' ? {id:v.id,until:v.until} : null; }
    catch { return null; }
  };
  let locked = false;
  const tick = () => {
    if (stopped) return;
    const current = read(), now = Date.now();
    if (current?.id === id) {
      try { localStorage.setItem(key, JSON.stringify({id, until:now + 90_000})); set(true); } catch { set(false); }
    } else if (!current || current.until < now) {
      set(false);
      try {
        localStorage.setItem(key, JSON.stringify({id, until:now + 90_000}));
        clearTimeout(settle);
        settle = setTimeout(() => set(read()?.id === id), 150);
      } catch { /* No shared coordination: stay silent rather than multiply alerts. */ }
    } else set(false);
  };
  const storage = (event: StorageEvent) => { if (event.key === key || event.key === null) tick(); };
  const fallback = () => {
    if (stopped) return;
    tick(); timer = setInterval(tick, 15_000);
    window.addEventListener('storage', storage);
  };
  if (navigator.locks) {
    void navigator.locks.request(key, {signal:abort.signal}, async () => {
      if (stopped) return;
      locked = true; set(true);
      await new Promise<void>(resolve => { release = resolve; });
      locked = false;
    }).catch((error: unknown) => { if (!(error instanceof DOMException && error.name === 'AbortError')) fallback(); });
  } else fallback();
  const stop = () => {
    if (stopped) return;
    changed(false); primary = false; stopped = true;
    abort.abort(); release?.(); clearInterval(timer); clearTimeout(settle);
    window.removeEventListener('storage',storage);
    window.removeEventListener('pagehide',stop);
    if (!locked && read()?.id === id) { try { localStorage.removeItem(key); } catch { /* private mode */ } }
  };
  window.addEventListener('pagehide',stop);
  return { isPrimary: () => !stopped && primary && (locked || read()?.id === id), stop };
}
